import type {
  Aggregates,
  AreaId,
  AreaOption,
  FollowUpBlock,
  RatingBlock,
  Traffic,
} from '../types/pulse'

export const pulse = {
  eyebrow: 'NOK Eye Care Pulse',
  railTitle: ['A shared', 'pulse.'],
  railBlurb:
    'A five-question, self-guided pulse for delegates at NOK 2026. Every answer is followed immediately by how the room has answered so far.',
  railDisclaimer:
    'Responses are anonymous. No name, email or hospital is collected. Aggregated findings will be shared back after the congress.',
  footer: 'Supported by AbbVie',
  dataLabel: 'Live results',
}

export const opening = {
  eyebrow: 'NOK Eye Care Pulse',
  headline: 'Eye care is under growing pressure',
  support: 'More patients. More follow-up. Limited specialist time.',
  prompt: 'Where do you think the biggest gains can be made?',
  promise:
    'Answer 5 quick questions and see how your view compares with other NOK delegates after every answer.',
  cta: 'Add your perspective',
  footnote:
    'Responses are aggregated and will be used to build a collective picture of priorities across NOK.',
}

export const ranking = {
  question: 'Where in the patient journey do you see the biggest opportunity to improve outcomes?',
  instruction: 'Rank all three — 1 = biggest opportunity, 3 = smaller opportunity.',
  cta: 'See what your peers think',
  resultLead: 'Your #1 priority',
  peerTitle: 'How NOK delegates rank the opportunity so far',
  peerNote: 'Share of delegates ranking each area #1.',
  resultCta: 'Go deeper',
}

export const areas: AreaOption[] = [
  {
    id: 'diagnosis',
    label: 'Diagnosis',
    blurb:
      'Identifying the right condition and understanding the individual patient well enough to guide care.',
  },
  {
    id: 'treatment',
    label: 'Treatment',
    blurb:
      'Choosing and delivering effective treatment with the least possible burden for patient and clinic.',
  },
  {
    id: 'followup',
    label: 'Follow-up',
    blurb:
      'Monitoring patients efficiently and identifying when closer attention or a change in care is needed.',
  },
]

export const followUps: Record<AreaId, FollowUpBlock> = {
  diagnosis: {
    question: 'Where is the biggest opportunity within diagnosis?',
    peerLead: 'diagnosis',
    cta: 'Compare with peers',
    options: [
      {
        id: 'earlier',
        label: 'Earlier diagnosis',
        blurb: 'Identifying patients who need specialist care sooner.',
      },
      {
        id: 'precise',
        label: 'More precise diagnosis',
        blurb: 'Understanding the individual patient and disease more accurately.',
      },
      {
        id: 'imaging',
        label: 'Better use of imaging and data',
        blurb: 'Getting more useful information from the tests already being performed.',
      },
      {
        id: 'other',
        label: 'Something else',
        blurb: 'Add it in your own words.',
        free: true,
      },
    ],
  },
  treatment: {
    question: 'Where is the biggest opportunity within treatment?',
    peerLead: 'treatment',
    cta: 'Compare with peers',
    options: [
      {
        id: 'earlier',
        label: 'Choosing the right treatment earlier',
        blurb: 'Getting patients onto the most appropriate treatment with less trial-and-error.',
      },
      {
        id: 'outcomes',
        label: 'Improving treatment outcomes',
        blurb: 'Achieving stronger or more consistent clinical benefit.',
      },
      {
        id: 'burden',
        label: 'Reducing treatment burden',
        blurb:
          'Reducing the frequency of treatments, procedures or treatment-related visits where appropriate.',
      },
      {
        id: 'other',
        label: 'Something else',
        blurb: 'Add it in your own words.',
        free: true,
      },
    ],
  },
  followup: {
    question: 'Where is the biggest opportunity within follow-up?',
    peerLead: 'follow-up',
    cta: 'Compare with peers',
    options: [
      {
        id: 'prioritise',
        label: 'Better prioritisation',
        blurb: 'Ensuring specialist attention goes to the patients who need it most.',
      },
      {
        id: 'efficient',
        label: 'More efficient monitoring',
        blurb: 'Following appropriate patients without unnecessary use of specialist time.',
      },
      {
        id: 'change',
        label: 'Earlier recognition of change',
        blurb: 'Identifying deterioration or the need to reassess care sooner.',
      },
      {
        id: 'other',
        label: 'Something else',
        blurb: 'Add it in your own words.',
        free: true,
      },
    ],
  },
}

export const followUpResult = {
  lead: 'You chose',
  freeTextAck: 'Thank you — your response has been added to the collective picture.',
  peerTitle: 'How the rest of the room answered',
  cta: 'Continue',
}

export const teamRating: RatingBlock = {
  question:
    'How much could better use of the wider eye-care team help relieve pressure on specialist care?',
  support:
    'For example, nurses, optometrists, technicians or primary care taking on appropriate parts of the pathway.',
  lowLabel: 'Very little',
  highLabel: 'A great deal',
  cta: 'See the NOK view',
  resultCta: 'Next question',
}

export const techRating: RatingBlock = {
  question:
    'How much potential do you see in technology freeing specialist time for the patients who need it most?',
  support:
    'For example through better triage, imaging analysis, remote monitoring, workflow automation or decision support.',
  lowLabel: 'Very little potential',
  highLabel: 'Very high potential',
  cta: 'Compare with peers',
  resultCta: 'One last question',
}

export const ratingResult = {
  yourLead: 'Your rating',
  averageLead: 'NOK average so far',
  highNote: 'of delegates rated this 4 or 5.',
}

export const openQuestion = {
  question: 'If you could change one thing in eye care tomorrow, what would it be?',
  support: 'No predefined answer is required. One sentence is enough.',
  placeholder: 'Type your answer…',
  promptLead: 'Or tap an idea that resonates with you',
  promptNote: 'Ideas other delegates have raised. Tapping one adds it — you can still edit it.',
  cta: 'Add my voice',
}

export const openResult = {
  headline: 'Thank you. Your voice has been added.',
  recapLead: 'You added',
  clusterLead: 'Others are talking about similar themes too.',
  note: 'Emerging themes from responses submitted so far.',
  cta: 'See the NOK picture',
}

export const snapshot = {
  headline: 'The NOK Eye Care Pulse — so far',
  rows: {
    area: 'Biggest opportunity in the patient journey',
    priority: 'Top priority within that area',
    team: 'Potential of wider care-team models',
    tech: 'Potential of technology',
    theme: 'Most common open-response theme',
  },
  closing: [
    'Thank you for contributing your perspective.',
    'The collective findings will be shared back after NOK.',
  ],
  footer: 'Supported by AbbVie',
  restart: 'Start a new response',
}

/** Responses collected at the congress before the current delegate. */
export const seedAggregates: Aggregates = {
  rankFirst: {
    diagnosis: 29,
    treatment: 44,
    followup: 37,
  },
  followUp: {
    diagnosis: { earlier: 12, precise: 9, imaging: 6, other: 2 },
    treatment: { earlier: 17, outcomes: 11, burden: 14, other: 2 },
    followup: { prioritise: 14, efficient: 12, change: 9, other: 2 },
  },
  team: { n: 108, sum: 432, high: 71 },
  tech: { n: 104, sum: 426, high: 73 },
  openResponses: 96,
  themes: [
    {
      id: 'capacity',
      label: 'Specialist capacity',
      prompt: 'More specialist time',
      count: 24,
      match: ['capacity', 'specialist time', 'time', 'staff', 'resource', 'workload'],
    },
    {
      id: 'access',
      label: 'Patient access',
      prompt: 'Faster access to care',
      count: 19,
      match: ['access', 'wait', 'waiting', 'referral', 'refer', 'queue'],
    },
    {
      id: 'burden',
      label: 'Treatment burden',
      prompt: 'Less treatment burden',
      count: 17,
      match: ['burden', 'injection', 'visits', 'frequency', 'interval', 'durable'],
    },
    {
      id: 'monitoring',
      label: 'Follow-up burden',
      prompt: 'Smarter follow-up',
      count: 14,
      match: ['follow-up', 'follow up', 'monitor', 'monitoring', 'control', 'recall'],
    },
    {
      id: 'pathways',
      label: 'Shared care pathways',
      prompt: 'Better use of the care team',
      count: 11,
      match: ['optometrist', 'nurse', 'team', 'primary care', 'shared', 'task'],
    },
    {
      id: 'admin',
      label: 'Administration',
      prompt: 'Less administration',
      count: 9,
      match: ['admin', 'paperwork', 'documentation', 'journal', 'system', 'booking'],
    },
  ],
}

/** How delegates reached the pulse. Congress telemetry, not linked to answers. */
export const traffic: Traffic = {
  day: [
    { id: 'thu', label: 'Thursday', count: 38 },
    { id: 'fri', label: 'Friday', count: 52 },
    { id: 'sat', label: 'Saturday', count: 21 },
  ],
  daypart: [
    { id: 'morning', label: 'Morning', count: 46 },
    { id: 'afternoon', label: 'Afternoon', count: 52 },
    { id: 'evening', label: 'Evening', count: 13 },
  ],
  source: [
    { id: 'qr', label: 'QR code on stand', count: 74 },
    { id: 'email', label: 'Email invitation', count: 24 },
    { id: 'rep', label: 'Rep-shared link', count: 13 },
  ],
}

export const dataView = {
  open: 'Live results',
  title: 'NOK Eye Care Pulse — live results',
  subtitle: 'Everything delegates have submitted so far, updated as responses arrive.',
  sections: {
    journey: 'Biggest opportunity in the patient journey',
    followUp: 'Priorities within each area',
    ratings: 'Operational potential',
    themes: 'Open-response themes',
    traffic: 'How delegates reached the pulse',
  },
  trafficLabels: {
    day: 'Day of congress',
    daypart: 'Time of day',
    source: 'Entry point',
  },
  close: 'Close',
}
